import React from 'react';

import { Check, Discount } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, FieldErrors, UseFormReturn } from 'react-hook-form';
import { get, isEmpty, map, toNumber } from 'lodash';
import dayjs from 'dayjs';
import numeral from 'numeral';
import { formatDate, formatTime } from '../../utils/libs';
import FormActions from './form_actions';
import PaymentFormDialog from './payment_form_dialog';
import { apiAxios, LoadingButton } from '../../components';
import { CreateGuestJobValueType } from './page';
import { createScopedI18n } from '../../i18n/i18n';

const pageI18n = createScopedI18n('pages.fast_track.payment_form', { joinOutput: true });

interface PaymentFormProps {
  form: UseFormReturn<CreateGuestJobValueType>;
  onNext?: () => void;
  onPrevious: () => void;
  onComplete?: (data: { referenceId?: string; otpFormHtml?: string }) => void;
  onError: (errors?: FieldErrors<CreateGuestJobValueType>) => void;
  loading?: boolean;
}

const UNIT_AMOUNT = 699;

const PaymentForm: React.FC<PaymentFormProps> = ({ form, onNext, onPrevious, onComplete, onError, loading }) => {
  const {
    control,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { isSubmitting, isSubmitSuccessful },
  } = form;

  const [showPaymentDialog, setShowPaymentDialog] = React.useState(false);
  const [policyAccepted, setPolicyAccepted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const scheduleDates = watch('scheduleDates');
  const numberOfPosition = watch('numberOfPosition');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const jobRefs = watch('jobRefs');

  const discountCode = watch('discountCode');
  const [discountLoading, setDiscountLoading] = React.useState(false);
  const [discountAmount, setDiscountAmount] = React.useState<number | null>();

  const amount = UNIT_AMOUNT * numberOfPosition;
  const totalAmount = amount * (scheduleDates?.length ?? 0);
  const finalAmount = totalAmount - (discountAmount ?? 0);

  React.useEffect(() => {
    if (!isSubmitting && isSubmitSuccessful) {
      setShowPaymentDialog(true);
    }
  }, [isSubmitting, isSubmitSuccessful]);

  React.useEffect(() => {
    setDiscountAmount(null);
  }, [discountCode]);

  const fetchDiscountAmount = async () => {
    if (isEmpty(discountCode)) {
      return;
    }

    let discount: number | null = null;
    setDiscountLoading(true);
    clearErrors('discountCode');
    setDiscountAmount(null);
    try {
      const response = await apiAxios({
        method: 'get',
        url: `/discounts/${discountCode}`,
      });

      discount = toNumber(get(response.data, 'data.amount', 0));
      if (discount) {
        setDiscountAmount(discount);
      }
    } catch (error) {
      setError('discountCode', { message: pageI18n('discount.unavailable') });
    }
    setDiscountLoading(false);
  };

  const handleDialogDismiss = async (reason: 'close' | 'submit') => {
    setShowPaymentDialog(false);

    if (reason === 'submit') {
      const token = getValues('token');

      setSubmitting(true);
      try {
        const response = await apiAxios({
          method: 'post',
          url: '/charge_credit_card_699',
          data: {
            token,
            satang: finalAmount * 100,
            job_references: jobRefs,
            discount_code: discountCode ?? null,
          },
        });

        const { data } = response.data;
        const referenceId = get(data, 'reference_id');
        const otpFormHtml = get(data, 'otp_form_html');

        if (referenceId) {
          setValue('referenceId', referenceId, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue('otpFormHtml', otpFormHtml);
          onComplete?.({ referenceId, otpFormHtml });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching token:', error);
      }
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    const isValid = await form.trigger();

    if (discountCode && !discountAmount) {
      await fetchDiscountAmount();
    }

    if (isValid) {
      onNext?.();
    } else {
      onError();
    }
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <List>
        {map(
          scheduleDates?.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()),
          (date: Date, index: number) => (
            <ListItem key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <ListItemText
                primary={formatDate(date)}
                secondary={`${formatTime(startTime)} - ${formatTime(endTime)}`}
              />
              <ListItemText
                primary={pageI18n('amount', { amount: numeral(amount).format('0,0') })}
                secondary={pageI18n('position', { count: numberOfPosition })}
                sx={{ flex: 1, textAlign: 'right' }}
              />
            </ListItem>
          ),
        )}

        <Collapse in={(discountAmount ?? 0) > 0}>
          <Divider />
          <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ padding: 1, paddingLeft: 0 }}>
              {pageI18n('discount.discount')}
            </Typography>
            <Typography variant="body1" sx={{ padding: 1, paddingRight: 0 }}>
              {pageI18n('amount', { amount: numeral(-(discountAmount ?? 0)).format('0,0') })}
            </Typography>
          </ListItem>
        </Collapse>

        <Divider />

        <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ padding: 1, paddingLeft: 0 }}>
            {pageI18n('total')}
          </Typography>
          <Typography variant="body1" sx={{ padding: 1, paddingRight: 0 }}>
            {pageI18n('amount', { amount: numeral(finalAmount).format('0,0') })}
          </Typography>
        </ListItem>

        <Divider />

        <ListItem>
          <Controller
            key="discountCode"
            name="discountCode"
            control={control}
            render={({ field: { onBlur, ...field }, fieldState: { error, invalid } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{pageI18n('discount.discount_code')}</FormLabel>
                  </Grid>
                  <Grid item xs={true}>
                    <TextField
                      id={field.name}
                      type="text"
                      fullWidth
                      error={invalid}
                      helperText={error?.message}
                      onBlur={() => {
                        onBlur();
                        fetchDiscountAmount();
                      }}
                      {...field}
                      InputProps={{
                        onKeyDown: (e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            fetchDiscountAmount();
                          }
                        },
                        endAdornment: (
                          <LoadingButton
                            size="small"
                            loading={discountLoading}
                            disabled={isEmpty(discountCode) || discountLoading}
                            startIcon={discountAmount ? <Check /> : <Discount />}
                            onClick={fetchDiscountAmount}
                          >
                            {pageI18n('discount.use_discount_code')}
                          </LoadingButton>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />
        </ListItem>
      </List>

      <PaymentFormDialog
        open={showPaymentDialog}
        form={form}
        amount={UNIT_AMOUNT}
        loading={loading || submitting}
        onClose={handleDialogDismiss}
      />

      <FormControlLabel
        control={<Checkbox checked={policyAccepted} onChange={(e) => setPolicyAccepted(e.target.checked)} />}
        label={
          <Typography variant="overline">
            {`${pageI18n('footer.agreement')} `}
            <Link href="https://www.findtemp.in.th/fast-track-term-condition" target="_blank" rel="noopener">
              {`${pageI18n('footer.terms_of_service')}`}
            </Link>
          </Typography>
        }
      />

      <FormActions
        nextButtonType="submit"
        nextButtonText="ชำระเงิน"
        nextButtonDisabled={!policyAccepted}
        loading={loading || submitting}
        onNext={handleNext}
        onPrevious={handlePrevious}
      />
    </Box>
  );
};

export default PaymentForm;
